/**
 * Detail collapse for an activity.
 */
import * as React from 'react';
import { Tag, Calendar, Info, Users } from 'lucide-react';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailsCard } from '@/components/common/DetailsCard';
import {
  ACTIVITY_STATUS_CONFIG,
  getActivityStatusColor,
} from '@/components/activities/activity-configs';
import type { Activity } from '@/types/activity.types';

/** Expanded-row collapse body for one activity. */
export const renderActivityDetails = (activity: Activity): React.ReactNode => {
  const statusConfig = ACTIVITY_STATUS_CONFIG[activity.status] || ACTIVITY_STATUS_CONFIG.active;
  const StatusIcon = statusConfig.icon;

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <Tag className="w-6 h-6 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">{activity.label}</h3>
              <DetailBadge
                label={statusConfig.label}
                icon={StatusIcon}
                colorName={getActivityStatusColor(activity.status)}
                variant="filled"
              />
            </div>
            <p className="details-view-header-subtitle">{activity.code}</p>
          </div>
        </div>
      </div>

      <div className="details-view-grid">
        <DetailsCard icon={Info} title="Informations générales" colorName="color-2" colSpan={1}>
          <div className="space-y-3">
            <DetailField label="Code" value={activity.code} />
            <DetailField label="Libellé" value={activity.label} />
            {activity.description && (
              <DetailField label="Description" value={activity.description} vertical />
            )}
          </div>
        </DetailsCard>

        <DetailsCard icon={Users} title="Configuration" colorName="color-1" colSpan={1}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4"></div>
        </DetailsCard>

        {activity.createdAt && (
          <DetailsCard
            icon={Calendar}
            title="Informations système"
            colorName="color-2"
            colSpan="full"
          >
            <div className="grid grid-cols-2 gap-4">
              <DetailField
                label="Créé le"
                value={new Date(activity.createdAt).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              />
              {activity.updatedAt && (
                <DetailField
                  label="Modifié le"
                  value={new Date(activity.updatedAt).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                />
              )}
            </div>
          </DetailsCard>
        )}
      </div>
    </>
  );
};
